/**
 * Directory module routes — moved verbatim from router/index.tsx.
 */

import { Navigate, type RouteObject } from 'react-router-dom';
import { PK } from '@/constants/permissions';
import { ROUTES } from '@/constants';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { PermissionRoute } from '../PermissionRoute';
import { LazyPage } from '../layouts';

// Directory pages
const CustomersPage = lazyWithRetry(() => import('@/features/directory/pages/CustomersPage'));
const ContainersPage = lazyWithRetry(() => import('@/features/directory/pages/ContainersPage'));
const EquipmentPage = lazyWithRetry(() => import('@/features/directory/pages/EquipmentPage'));
const PtoDirectoryPage = lazyWithRetry(() => import('@/features/directory/pages/PtoPage'));
const ServicesPage = lazyWithRetry(() => import('@/features/directory/pages/ServicesPage'));
const DirectionsPage = lazyWithRetry(() => import('@/features/directory/pages/DirectionsPage'));
const MaterialsPage = lazyWithRetry(() => import('@/features/directory/pages/MaterialsPage'));
// Vision v4 Phase 1 — Stations & Terminals directory
const StationsPage = lazyWithRetry(() => import('@/features/directory/stations/pages/StationsPage'));
const TerminalsPage = lazyWithRetry(() => import('@/features/directory/terminals/pages/TerminalsPage'));
// Vision v4 Phase 2 — Pricing
const PricingPage = lazyWithRetry(() => import('@/features/directory/pricing/pages/PricingPage'));
// Inspection redesign, Phase 2 — container attributes & inspection reasons
const ContainerAttributesPage = lazyWithRetry(() => import('@/features/directory/container-attributes/pages/ContainerAttributesPage'));
const InspectionReasonsPage = lazyWithRetry(() => import('@/features/directory/inspection-reasons/pages/InspectionReasonsPage'));
// PTO crew, Phase 4 — mechanic types directory
const MechanicTypesPage = lazyWithRetry(() => import('@/features/directory/mechanic-types/pages/MechanicTypesPage'));

export const directoryRoutes: RouteObject[] = [
      // --------------------------------
      // Directory Module
      // --------------------------------
      {
        path: ROUTES.directory.index,
        element: <Navigate to={ROUTES.directory.customers.list} replace />,
      },
      // Customers
      {
        path: ROUTES.directory.customers.list,
        element: (
          <PermissionRoute permission={PK.Customers.View}>
            <CustomersPage />
          </PermissionRoute>
        ),
      },
      // The same list with one customer's card open — where «Компания» in the ЭУ and container
      // directories leads.
      {
        path: ROUTES.directory.customers.details,
        element: (
          <PermissionRoute permission={PK.Customers.View}>
            <CustomersPage />
          </PermissionRoute>
        ),
      },
      // Containers
      {
        path: ROUTES.directory.containers.list,
        element: (
          <PermissionRoute permission={PK.Containers.View}>
            <ContainersPage />
          </PermissionRoute>
        ),
      },
      // Equipment
      {
        path: ROUTES.directory.equipment.list,
        element: (
          <PermissionRoute permission={PK.Equipment.View}>
            <EquipmentPage />
          </PermissionRoute>
        ),
      },
      // Services
      {
        path: ROUTES.directory.services.list,
        element: (
          <PermissionRoute permission={PK.Services.View}>
            <ServicesPage />
          </PermissionRoute>
        ),
      },
      // The same list with one service's card open — where the 1C nomenclature list leads.
      {
        path: ROUTES.directory.services.details,
        element: (
          <PermissionRoute permission={PK.Services.View}>
            <ServicesPage />
          </PermissionRoute>
        ),
      },
      // PTO Directory
      {
        path: ROUTES.directory.pto.list,
        element: (
          <PermissionRoute permission={PK.Pto.View}>
            <PtoDirectoryPage />
          </PermissionRoute>
        ),
      },
      // Directions
      {
        path: ROUTES.directory.directions.list,
        element: (
          <PermissionRoute permission={PK.Directions.View}>
            <DirectionsPage />
          </PermissionRoute>
        ),
      },
      // Materials
      {
        path: ROUTES.directory.materials.list,
        element: (
          <PermissionRoute permission={PK.Materials.View}>
            <MaterialsPage />
          </PermissionRoute>
        ),
      },

      // Stations (Vision v4 Phase 1)
      {
        path: ROUTES.directory.stations.list,
        element: (
          <PermissionRoute permission={PK.Stations.View}>
            <StationsPage />
          </PermissionRoute>
        ),
      },
      // Terminals (Vision v4 Phase 1)
      {
        path: ROUTES.directory.terminals.list,
        element: (
          <PermissionRoute permission={PK.Terminals.View}>
            <TerminalsPage />
          </PermissionRoute>
        ),
      },
      // Pricing (Vision v4 Phase 2)
      {
        path: ROUTES.directory.pricing.list,
        element: (
          <PermissionRoute permission={PK.Pricing.View}>
            <LazyPage><PricingPage /></LazyPage>
          </PermissionRoute>
        ),
      },
      // Container attributes (inspection redesign, Phase 2)
      {
        path: ROUTES.directory.containerAttributes.list,
        element: (
          <PermissionRoute permission={PK.ContainerAttributes.View}>
            <ContainerAttributesPage />
          </PermissionRoute>
        ),
      },
      // Inspection reasons (inspection redesign, Phase 2)
      {
        path: ROUTES.directory.inspectionReasons.list,
        element: (
          <PermissionRoute permission={PK.InspectionReasons.View}>
            <InspectionReasonsPage />
          </PermissionRoute>
        ),
      },
      // Mechanic types (PTO crew, Phase 4)
      {
        path: ROUTES.directory.mechanicTypes.list,
        element: (
          <PermissionRoute permission={PK.MechanicTypes.View}>
            <MechanicTypesPage />
          </PermissionRoute>
        ),
      },
];
